import { formatDate, fromDayNumber, toDayNumber } from "@/modules/carga-trabalho";
import type { CargaTrabalhoEtapas, CargaTrabalhoIntervalo } from "@/modules/carga-trabalho";
import { identificacaoDaObra } from "@/shared/utils/formatar-identificacao-obra";
import type { IdentificacaoObra } from "@/shared/utils/formatar-identificacao-obra";
import type { CargaAtualComparacao } from "./simulacao.carga-atual";
import {
  chaveAlteracao,
  diffEquipe,
  membrosComEfeito,
  parearEquipe,
  paresComEfeito,
} from "./simulacao.model";
import type { AlteracaoSimulada, CenarioSimulacao, ObraSimulada } from "./simulacao.model";
import {
  CATEGORIA_LABEL,
  CONFLITO_EM_ABERTO,
  CONFLITO_TRATADO,
  formatarPeriodo,
  LIBERADO,
  mensagemRemocaoVinculada,
  NAO_INFORMADO,
  ROTULO_CAMPO,
  ROTULO_REMOVIDO,
  rotuloObra,
  SEM_RECURSO,
  TITULO_ACAO,
  vinculoSubstituicao,
} from "./simulacao.textos";

/**
 * Leituras do cenário para Comparar e Aplicar Simulação (HU 2221): o que
 * mudou, em linguagem de tela, e o resumo por recurso. O Comparar usa a carga
 * ao vivo quando ela vem da perspectiva; sem carga, cai no `anterior` do item.
 */

export type CategoriaAlteracao = "profissional" | "fornecedor" | "local";

export function categoriaDaAlteracao(alteracao: AlteracaoSimulada): CategoriaAlteracao {
  switch (alteracao.acao) {
    case "definir-equipe":
    case "remanejar-profissional":
    case "ignorar-conflito-profissional":
      return "profissional";
    case "remanejar-fornecedor":
    case "ignorar-conflito-fornecedor":
      return "fornecedor";
    case "remanejar-local":
    case "ignorar-conflito-local":
      return "local";
  }
}

/** Obras que a alteração afeta. */
export function obrasDaAlteracao(alteracao: AlteracaoSimulada): ObraSimulada[] {
  return "obras" in alteracao ? alteracao.obras : [alteracao.obra];
}

/** Um campo do cartão (rótulo e valor), na ordem da HU. */
export interface CampoCartao {
  rotulo: string;
  valor: string;
  /** Valor de antes, quando a alteração o muda: o cartão o mostra riscado, antes do novo. */
  anterior?: string;
}

/**
 * Cartão do Aplicar Simulação. Uma substituição (RN009) rende dois cartões:
 * o principal, com quem entra, e o da remoção vinculada, com quem sai — esse não se desfaz
 * por si (MS006); "Desfazer alteração do substituto" desfaz a substituição inteira.
 * A contagem (faixa, Comparar, Aplicar N) é 1 por instrução, não por cartão.
 */
export interface CartaoAlteracao {
  id: string;
  /** Chave da alteração que o cartão representa (o Desfazer age sobre ela). */
  chave: string;
  categoria: CategoriaAlteracao;
  titulo: string;
  /** Nome do recurso do cartão (profissional, fornecedor ou local). */
  recurso: string;
  campos: CampoCartao[];
  /** Só na remoção vinculada: o que ela libera, a orientação (MS006) e o vínculo com o substituto. */
  removido: { liberado: string; aviso: string; vinculo: string } | null;
  /** RN011/RN030: os conflitos novos que ela cria já foram justificados. */
  jaJustificado: boolean;
  /** Voltou de uma aplicação sem ser gravada ("Ajustar na simulação"): o motivo. */
  ajustePendente: string | null;
  /** Conflitos que o recurso do cartão continua tendo depois de aplicar (tag "N Conflito"). */
  conflitos: number;
}

function campo(
  rotulo: string,
  valor: string | null | undefined,
  anterior?: string | null,
): CampoCartao {
  const antes = anterior && anterior !== valor ? anterior : undefined;
  return antes
    ? { rotulo, valor: valor || NAO_INFORMADO, anterior: antes }
    : { rotulo, valor: valor || NAO_INFORMADO };
}

/** Campo que o front nem sempre conhece (Papel, Escopo Macro): sem o dado, fica de fora. */
function campoSeHouver(rotulo: string, valor: string | null | undefined): CampoCartao[] {
  return valor ? [{ rotulo, valor }] : [];
}

function datas(
  intervalo: CargaTrabalhoIntervalo,
  anterior?: CargaTrabalhoIntervalo,
): CampoCartao[] {
  return [
    campo(
      ROTULO_CAMPO.inicio,
      formatDate(intervalo.inicio),
      anterior && formatDate(anterior.inicio),
    ),
    campo(ROTULO_CAMPO.fim, formatDate(intervalo.fim), anterior && formatDate(anterior.fim)),
  ];
}

function comConflitos(cartoes: CartaoAlteracao[], alteracao: AlteracaoSimulada): CartaoAlteracao[] {
  const residuais = alteracao.conflitosResiduais;
  if (!residuais) return cartoes;
  const principais = cartoes.filter((cartao) => !cartao.removido);
  const sem = principais.every((cartao) => residuais[cartao.recurso] === undefined);
  const total = Object.values(residuais).reduce((soma, n) => soma + n, 0);
  return cartoes.map((cartao) =>
    cartao.removido
      ? cartao
      : {
          ...cartao,
          // Sem o nome do recurso nos conflitos (ex.: variação do nome), o cartão único leva o total.
          conflitos: sem && principais.length === 1 ? total : (residuais[cartao.recurso] ?? 0),
        },
  );
}

export function cartoesDaAlteracao(alteracao: AlteracaoSimulada): CartaoAlteracao[] {
  return comConflitos(montarCartoes(alteracao), alteracao);
}

function montarCartoes(alteracao: AlteracaoSimulada): CartaoAlteracao[] {
  const chave = chaveAlteracao(alteracao);
  const categoria = categoriaDaAlteracao(alteracao);
  const obra = campo(ROTULO_CAMPO.obra, obrasDaAlteracao(alteracao).map(rotuloObra).join(" × "));
  const base = {
    chave,
    categoria,
    jaJustificado: Boolean(alteracao.justificativaConflitos),
    ajustePendente: alteracao.ajustePendente ?? null,
    conflitos: 0,
  };
  const remocao = (
    id: string,
    recurso: string,
    campos: CampoCartao[],
    substituto: string,
  ): CartaoAlteracao => ({
    ...base,
    id: `${chave}:removido:${id}`,
    titulo: ROTULO_REMOVIDO,
    recurso,
    campos,
    removido: {
      liberado: LIBERADO[categoria],
      aviso: mensagemRemocaoVinculada(substituto),
      vinculo: vinculoSubstituicao(substituto, recurso),
    },
  });

  switch (alteracao.acao) {
    case "definir-equipe": {
      const diff = diffEquipe(alteracao.anterior, alteracao.novo);
      const substitutos = alteracao.novo.map((membro) => membro.nome).join(", ");
      return [
        ...paresComEfeito(alteracao.anterior, alteracao.novo).map(
          ({ membro, previa }): CartaoAlteracao => ({
            ...base,
            id: `${chave}:${membro.alocacaoId ?? previa?.alocacaoId ?? membro.usuarioId}`,
            titulo: TITULO_ACAO[alteracao.acao],
            recurso: membro.nome,
            campos: [
              obra,
              ...campoSeHouver(ROTULO_CAMPO.papel, membro.papel),
              ...datas(membro, previa),
            ],
            removido: null,
          }),
        ),
        // Quem segue na equipe só teve o período mudado (campo riscado no cartão): não é remoção.
        ...diff.removed.map((membro) =>
          remocao(
            membro.alocacaoId ?? membro.usuarioId,
            membro.nome,
            [
              obra,
              ...campoSeHouver(ROTULO_CAMPO.papel, membro.papel),
              campo(ROTULO_CAMPO.alocacao, formatarPeriodo(membro)),
            ],
            substitutos,
          ),
        ),
      ];
    }
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local": {
      const recurso = CATEGORIA_LABEL[categoria];
      const novo = campo(recurso, alteracao.novo.nome, alteracao.anterior.nome);
      const papel = alteracao.acao === "remanejar-profissional" ? alteracao.papel : undefined;
      const escopo = alteracao.acao === "remanejar-fornecedor" ? alteracao.escopoMacro : undefined;
      const principal: CampoCartao[] = [
        obra,
        ...campoSeHouver(ROTULO_CAMPO.papel, papel),
        ...campoSeHouver(ROTULO_CAMPO.escopoMacro, escopo),
        novo,
        ...datas(alteracao.periodo),
      ];
      return [
        {
          ...base,
          id: chave,
          titulo: TITULO_ACAO[alteracao.acao],
          recurso: alteracao.novo.nome,
          campos: principal,
          removido: null,
        },
        remocao(
          alteracao.anterior.id,
          alteracao.anterior.nome,
          [
            obra,
            ...campoSeHouver(ROTULO_CAMPO.papel, papel),
            ...campoSeHouver(ROTULO_CAMPO.escopoMacro, escopo),
            campo(ROTULO_CAMPO.alocacao, formatarPeriodo(alteracao.periodo)),
          ],
          alteracao.novo.nome,
        ),
      ];
    }
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local": {
      const recurso =
        alteracao.acao === "ignorar-conflito-profissional"
          ? alteracao.profissional.nome
          : alteracao.acao === "ignorar-conflito-fornecedor"
            ? alteracao.fornecedor.nome
            : alteracao.local.nome;
      return [
        {
          ...base,
          id: chave,
          titulo: TITULO_ACAO[alteracao.acao],
          recurso,
          campos: [obra, ...datas(alteracao.conflito)],
          removido: null,
        },
      ];
    }
  }
}

/** Remanejo (substituição) conta 2: quem entra e quem sai (comentário do Marco na HU 2220, 01/10). */
function pesoDasLinhas(alteracao: AlteracaoSimulada, linhas: number): number {
  return alteracao.acao.startsWith("remanejar-") ? linhas * 2 : linhas;
}

/**
 * Peso da instrução na faixa, no Comparar e no Aplicar (N).
 * Remanejo conta 2, um por cartão do Aplicar (o que entra e o Removido). Definir equipe conta
 * quem de fato mudou (entrou, saiu ou período/papel).
 */
export function pesoDaAlteracao(alteracao: AlteracaoSimulada): number {
  return pesoDasLinhas(alteracao, linhasDaAlteracao(alteracao).length);
}

/**
 * Contagem única de alterações (faixa, Comparar, Aplicar N): 1 por linha do Comparar e 2 por
 * substituição. A `qtd_alteracoes` da API conta 1 por substituição; a faixa usa o maior dos dois.
 */
export function contarAlteracoes(cenario: CenarioSimulacao): number {
  return cenario.alteracoes.reduce((total, alteracao) => total + pesoDaAlteracao(alteracao), 0);
}

/** Recursos que continuam com conflito após aplicar (aviso amarelo do Resumo), um por nome. */
export function conflitosRestantes(
  cenario: CenarioSimulacao,
): { recurso: string; total: number }[] {
  const porRecurso = new Map<string, number>();
  for (const alteracao of cenario.alteracoes) {
    for (const cartao of cartoesDaAlteracao(alteracao)) {
      if (cartao.conflitos > 0) {
        porRecurso.set(cartao.recurso, (porRecurso.get(cartao.recurso) ?? 0) + cartao.conflitos);
      }
    }
  }
  return [...porRecurso].map(([recurso, total]) => ({ recurso, total }));
}

export interface ResumoSimulacao {
  alteracoes: number;
  /** Rótulos das obras afetadas, um por linha no painel. */
  obras: string[];
  profissionais: number;
  fornecedores: number;
  locais: number;
}

/** Painel Resumo do Aplicar Simulação: quantas alterações e quantos recursos distintos. */
export function resumirCenario(cenario: CenarioSimulacao): ResumoSimulacao {
  const obras = new Map<string, string>();
  const profissionais = new Set<string>();
  const fornecedores = new Set<string>();
  const locais = new Set<string>();

  for (const alteracao of cenario.alteracoes) {
    obrasDaAlteracao(alteracao).forEach((obra) => obras.set(obra.id, rotuloObra(obra)));
    switch (alteracao.acao) {
      case "definir-equipe": {
        const diff = diffEquipe(alteracao.anterior, alteracao.novo);
        [...membrosComEfeito(alteracao.anterior, alteracao.novo), ...diff.removed].forEach((m) =>
          profissionais.add(m.usuarioId),
        );
        break;
      }
      case "remanejar-profissional":
        profissionais.add(alteracao.anterior.id).add(alteracao.novo.id);
        break;
      case "ignorar-conflito-profissional":
        profissionais.add(alteracao.profissional.id);
        break;
      case "remanejar-fornecedor":
        fornecedores.add(alteracao.anterior.id).add(alteracao.novo.id);
        break;
      case "ignorar-conflito-fornecedor":
        fornecedores.add(alteracao.fornecedor.id);
        break;
      case "remanejar-local":
        locais.add(alteracao.anterior.id).add(alteracao.novo.id);
        break;
      case "ignorar-conflito-local":
        locais.add(alteracao.local.id);
        break;
    }
  }

  return {
    alteracoes: contarAlteracoes(cenario),
    obras: [...obras.values()],
    profissionais: profissionais.size,
    fornecedores: fornecedores.size,
    locais: locais.size,
  };
}

/* Comparar simulação com o cronograma atual. */

export interface VersaoComparada {
  rotulo: string;
  /** Recurso da linha nesta versão: quem ocupa a alocação antes (Atual) ou depois (Simulado). */
  recurso: string;
  /** Datas da alocação: a parte "obra" da barra e o texto dentro dela. */
  intervalo: CargaTrabalhoIntervalo | null;
  /** Planejamento e fechamento da obra (profissional); ausente em fornecedor e local. */
  etapas?: CargaTrabalhoEtapas;
}

export interface LinhaComparada {
  chave: string;
  categoria: CategoriaAlteracao;
  recurso: string;
  /** Papel (profissional) ou Escopo Macro (fornecedor), quando o front o conhece. */
  contexto: string | null;
  atual: VersaoComparada;
  simulado: VersaoComparada;
  /** Diferença do início simulado para o atual, em dias; `null` quando não mudou. */
  deslocamentoDias: number | null;
  /** Diferença do fim simulado para o atual, em dias; `null` quando não mudou. */
  deslocamentoFimDias: number | null;
}

export interface ObraComparada {
  id: string;
  rotulo: string;
  /** `ID - nome da embarcação` e `ID - sigla` (ADR 0018), as formas do rótulo de tela. */
  identificacao: IdentificacaoObra;
  /** Quantas alterações do cenário tocam a obra. */
  alteracoes: number;
  /** Etapas da obra, quando o cenário as conhece (indicadores do Comparar). */
  etapas: CargaTrabalhoEtapas | null;
  linhas: LinhaComparada[];
}

function diferencaEmDias(atual: string, simulado: string): number | null {
  const dias = toDayNumber(simulado) - toDayNumber(atual);
  return dias === 0 ? null : dias;
}

/**
 * Em cada versão (Atual ou Simulado) o planejamento termina no dia anterior ao início da obra
 * daquela versão e o fechamento começa no dia seguinte ao fim dela, sem vão; a obra em si é o
 * `intervalo`. A Atual usa as datas de antes, para não herdar o tamanho da Simulado.
 */
function etapasAteIntervalo(
  etapas: CargaTrabalhoEtapas | undefined,
  intervalo: CargaTrabalhoIntervalo,
): CargaTrabalhoEtapas | undefined {
  if (!etapas) return undefined;
  const { planejamento, fechamento } = etapas;
  const fimPlanejamento = fromDayNumber(toDayNumber(intervalo.inicio) - 1);
  const inicioFechamento = fromDayNumber(toDayNumber(intervalo.fim) + 1);
  return {
    planejamento:
      planejamento && fimPlanejamento >= planejamento.inicio
        ? { inicio: planejamento.inicio, fim: fimPlanejamento }
        : null,
    obra: etapas.obra,
    fechamento:
      fechamento && inicioFechamento <= fechamento.fim
        ? { inicio: inicioFechamento, fim: fechamento.fim }
        : null,
  };
}

function linha(
  chave: string,
  categoria: CategoriaAlteracao,
  recurso: string,
  atual: VersaoComparada,
  simulado: VersaoComparada,
  contexto?: string,
): LinhaComparada {
  return {
    chave,
    categoria,
    recurso,
    contexto: contexto ?? null,
    atual,
    simulado,
    deslocamentoDias:
      atual.intervalo && simulado.intervalo
        ? diferencaEmDias(atual.intervalo.inicio, simulado.intervalo.inicio)
        : null,
    deslocamentoFimDias:
      atual.intervalo && simulado.intervalo
        ? diferencaEmDias(atual.intervalo.fim, simulado.intervalo.fim)
        : null,
  };
}

function cobreCarga(
  carga: CargaAtualComparacao | undefined,
  categoria: CategoriaAlteracao,
): carga is CargaAtualComparacao {
  return Boolean(carga && carga.tipo === categoria);
}

function linhasDaAlteracao(
  alteracao: AlteracaoSimulada,
  carga?: CargaAtualComparacao,
): LinhaComparada[] {
  const chave = chaveAlteracao(alteracao);
  const categoria = categoriaDaAlteracao(alteracao);

  switch (alteracao.acao) {
    case "definir-equipe": {
      const vivos = cobreCarga(carga, "profissional")
        ? carga.alocacoes.filter((item) => item.obraId === alteracao.obra.id)
        : null;
      const { removed } = parearEquipe(alteracao.anterior, alteracao.novo);
      const pares = [
        ...paresComEfeito(alteracao.anterior, alteracao.novo),
        ...removed.map((antes) => ({ membro: undefined, previa: antes })),
      ];
      return pares.map(({ membro: depois, previa: antes }) => {
        const membro = depois ?? antes;
        const alocacaoId = depois?.alocacaoId ?? antes?.alocacaoId;
        // A alocação do par, não a primeira do profissional na obra (HU 2050, RN06).
        const vivo = vivos?.find((item) =>
          alocacaoId ? item.id === alocacaoId : item.recursoId === membro?.usuarioId,
        );
        const atual = vivos
          ? vivo
            ? {
                rotulo: vivo.recursoNome,
                recurso: vivo.recursoNome,
                intervalo: { inicio: vivo.inicio, fim: vivo.fim },
                etapas: etapasAteIntervalo(alteracao.obra.etapas, {
                  inicio: vivo.inicio,
                  fim: vivo.fim,
                }),
              }
            : { rotulo: SEM_RECURSO[categoria], recurso: SEM_RECURSO[categoria], intervalo: null }
          : antes
            ? {
                rotulo: antes.nome,
                recurso: antes.nome,
                intervalo: antes,
                etapas: etapasAteIntervalo(alteracao.obra.etapas, antes),
              }
            : { rotulo: SEM_RECURSO[categoria], recurso: SEM_RECURSO[categoria], intervalo: null };
        return linha(
          `${chave}:${alocacaoId ?? membro?.usuarioId}`,
          categoria,
          depois?.nome ?? vivo?.recursoNome ?? antes?.nome ?? "",
          atual,
          depois
            ? {
                rotulo: depois.nome,
                recurso: depois.nome,
                intervalo: depois,
                etapas: etapasAteIntervalo(alteracao.obra.etapas, depois),
              }
            : {
                rotulo: ROTULO_REMOVIDO,
                recurso: vivo?.recursoNome ?? antes?.nome ?? "",
                intervalo: null,
              },
          (depois ?? antes)?.papel ?? undefined,
        );
      });
    }
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local": {
      const etapas =
        alteracao.acao === "remanejar-profissional" ? alteracao.obra.etapas : undefined;
      const vivo = cobreCarga(carga, categoria)
        ? carga.alocacoes.find((item) => item.id === alteracao.alocacaoId)
        : undefined;
      const periodoAtual = vivo ? { inicio: vivo.inicio, fim: vivo.fim } : alteracao.periodo;
      const nomeAtual = vivo?.recursoNome ?? alteracao.anterior.nome;
      const atualAusente = cobreCarga(carga, categoria) && !vivo;
      return [
        linha(
          chave,
          categoria,
          alteracao.novo.nome,
          atualAusente
            ? { rotulo: SEM_RECURSO[categoria], recurso: SEM_RECURSO[categoria], intervalo: null }
            : {
                rotulo: nomeAtual,
                recurso: nomeAtual,
                intervalo: periodoAtual,
                etapas: etapasAteIntervalo(etapas, periodoAtual),
              },
          {
            rotulo: alteracao.novo.nome,
            recurso: alteracao.novo.nome,
            intervalo: periodoAtual,
            etapas: etapasAteIntervalo(etapas, periodoAtual),
          },
          alteracao.acao === "remanejar-fornecedor"
            ? alteracao.escopoMacro
            : alteracao.acao === "remanejar-profissional"
              ? alteracao.papel
              : undefined,
        ),
      ];
    }
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local": {
      const recurso =
        alteracao.acao === "ignorar-conflito-profissional"
          ? alteracao.profissional.nome
          : alteracao.acao === "ignorar-conflito-fornecedor"
            ? alteracao.fornecedor.nome
            : alteracao.local.nome;
      const ids = new Set(alteracao.alocacaoIds);
      const conflitoSumiu =
        cobreCarga(carga, categoria) && !carga.alocacoes.some((item) => ids.has(item.id));
      return [
        linha(
          chave,
          categoria,
          recurso,
          {
            rotulo: CONFLITO_EM_ABERTO,
            recurso,
            intervalo: conflitoSumiu ? null : alteracao.conflito,
          },
          { rotulo: CONFLITO_TRATADO, recurso, intervalo: alteracao.conflito },
        ),
      ];
    }
  }
}

/** Cada obra afetada, com as versões Atual e Simulado de cada recurso alterado nela. */
export function compararCenario(
  cenario: CenarioSimulacao,
  carga?: CargaAtualComparacao,
): ObraComparada[] {
  const obras = new Map<string, ObraComparada>();

  for (const alteracao of cenario.alteracoes) {
    const linhas = linhasDaAlteracao(alteracao, carga);
    for (const obra of obrasDaAlteracao(alteracao)) {
      const atual = obras.get(obra.id) ?? {
        id: obra.id,
        rotulo: rotuloObra(obra),
        identificacao: identificacaoDaObra(obra),
        alteracoes: 0,
        etapas: null,
        linhas: [],
      };
      atual.alteracoes += pesoDasLinhas(alteracao, linhas.length);
      if (alteracao.acao === "definir-equipe") atual.etapas = alteracao.obra.etapas;
      atual.linhas.push(...linhas);
      obras.set(obra.id, atual);
    }
  }

  return [...obras.values()];
}

export type FiltroComparacao = "todos" | CategoriaAlteracao;

/** Contagem de alterações por tipo, para o filtro do Comparar. */
export function contarPorCategoria(cenario: CenarioSimulacao): Record<FiltroComparacao, number> {
  const contagem: Record<FiltroComparacao, number> = {
    todos: contarAlteracoes(cenario),
    profissional: 0,
    fornecedor: 0,
    local: 0,
  };
  cenario.alteracoes.forEach((alteracao) => {
    contagem[categoriaDaAlteracao(alteracao)] += pesoDaAlteracao(alteracao);
  });
  return contagem;
}

export function filtrarComparacao(
  obras: ObraComparada[],
  filtro: FiltroComparacao,
): ObraComparada[] {
  if (filtro === "todos") return obras;
  return obras
    .map((obra) => ({ ...obra, linhas: obra.linhas.filter((l) => l.categoria === filtro) }))
    .filter((obra) => obra.linhas.length > 0);
}
